import { type FC } from "react";

import { cn } from "@/shared/lib/utils";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import type { FlowQuestion } from "@/shared/constants/flowTypes";

interface MultiSelectProps {
  question: FlowQuestion;
  value: string[];
  onChange: (value: string[]) => void;
  errorMessage?: string;
}

function findEntry(value: string[], optionValue: string): string | undefined {
  return value.find((v) => v === optionValue || v.startsWith(`${optionValue}:`));
}

function getTextFromEntry(entry: string, optionValue: string): string {
  return entry.startsWith(`${optionValue}:`) ? entry.slice(optionValue.length + 1) : "";
}

export const MultiSelect: FC<MultiSelectProps> = ({ question, value, onChange, errorMessage }) => {
  if (!question.options) return null;

  const handleToggle = (optionValue: string, hasTextInput?: boolean) => {
    const existing = findEntry(value, optionValue);
    if (existing) {
      onChange(value.filter((v) => v !== existing));
    } else {
      onChange([...value, hasTextInput ? `${optionValue}:` : optionValue]);
    }
  };

  const handleTextChange = (optionValue: string, text: string) => {
    const existing = findEntry(value, optionValue);
    const newEntry = `${optionValue}:${text}`;
    if (existing) {
      onChange(value.map((v) => (v === existing ? newEntry : v)));
    } else {
      onChange([...value, newEntry]);
    }
  };

  const hasImageCards = question.options.some((o) => o.imageSrc);

  const errorNode = <FieldError message={errorMessage} />;

  if (hasImageCards) {
    const cols = question.gridColumns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 md:grid-cols-3";

    return (
      <div className="flex flex-col gap-2">
        <div className={cn("grid grid-cols-1 gap-3", cols)}>
          {question.options.map((option) => {
            const isSelected = !!findEntry(value, option.value);

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex cursor-pointer overflow-hidden rounded-lg border-[1.5px] border-solid text-left transition-all",
                  "h-auto flex-row sm:h-[180px] sm:flex-col",
                  isSelected ? "border-focus" : "border-primary",
                  option.bgClass ?? "bg-white",
                )}
                onClick={() => handleToggle(option.value)}
              >
                <div className="flex flex-1 items-center gap-3 bg-white px-4 py-3 sm:min-h-[66px] sm:w-full sm:flex-initial">
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <span className="text-sm font-medium leading-[155%] text-primary">
                    {option.label}
                  </span>
                </div>
                {option.imageSrc && (
                  <div className="flex items-center justify-center p-3 sm:flex-1 sm:px-3 sm:pb-3 sm:pt-0">
                    <img
                      src={option.imageSrc}
                      alt={option.label || "Option illustration"}
                      className="h-16 w-16 object-contain sm:h-auto sm:w-auto sm:max-h-full"
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {errorNode}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3">
        {question.options.map((option) => {
          const entry = findEntry(value, option.value);
          const isSelected = !!entry;

          if (option.hasTextInput) {
            const textValue = entry ? getTextFromEntry(entry, option.value) : "";

            return (
              <div
                key={option.value}
                className={cn(
                  "w-full rounded-lg border-[1.5px] border-solid bg-white p-4 text-left transition-all",
                  isSelected ? "border-focus" : "border-primary",
                )}
              >
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-2"
                  onClick={() => handleToggle(option.value, true)}
                >
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <span className="text-sm text-primary">{option.label}</span>
                </button>
                {isSelected && (
                  <div className="mt-3">
                    <Input
                      value={textValue}
                      onChange={(e) => handleTextChange(option.value, e.target.value)}
                      placeholder={option.textInputPlaceholder ?? "Type here"}
                    />
                  </div>
                )}
              </div>
            );
          }

          return (
            <OptionCard
              key={option.value}
              label={option.label}
              icon={option.icon}
              selected={isSelected}
              onClick={() => handleToggle(option.value)}
              variant="checkbox"
            />
          );
        })}
      </div>
      {errorNode}
    </div>
  );
};
